import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  cancelBullseyeMatchRegistration,
  confirmBullseyeMatchRegistration,
  createOwnerManagedBullseyeMatchRegistration,
  getBullseyeMatch,
  getBullseyeMatchSetupDetail,
  getBullseyeMatchRegistrationSummary,
  listBullseyeMatchLeaderboards,
  listAllBullseyeMatchRegistrations,
  listCompetitionEntries,
  updateBullseyeMatch,
  updateBullseyeMatchRegistration,
  updateCompetitionEntry,
} from "../../lib/leaderboardApi";
import { buildAuthReturnPath, buildCurrentReturnTo } from "../../lib/authReturn";
import { getCompetitionAuthProfile } from "../../lib/competitionAuthProfile";
import { formatCalendarDateTime } from "../../utils/date";
import BullseyeNraIdentityFields, { validBullseyeCompetitorId } from "../Matches/BullseyeNraIdentityFields";
import { bullseyeEnrollmentEntries, confirmBullseyeEnrollment } from "./bullseyeEnrollment";
import RegistrationOrigin from "./RegistrationOrigin";
import "./Leaderboard.css";

const STATUS_FILTERS = ["all", "pending", "confirmed", "cancelled", "withdrawn"];
const MATCH_STATUS_OPTIONS = ["scheduled", "in_progress", "completed"];
const PUBLICATION_STATE_OPTIONS = ["published_private", "published_public"];
const FIREARM_CHOICES = [".22 Rimfire", "Center Fire", ".45"];

function formatDateTime(value) {
  return formatCalendarDateTime(value);
}

function formatStatus(value) {
  return String(value || "unknown")
    .trim()
    .replace(/_/g, " ")
    .replace(/^./, (char) => char.toUpperCase());
}

function isAuthErrorMessage(value) {
  const message = String(value || "").toLowerCase();
  return message.includes("sign in") || message.includes("authenticated") || message.includes("user needs to be authenticated");
}

function buildLocationLabel(match) {
  const primary = String(match?.location || "").trim();
  if (primary) return primary;

  const city = String(match?.city || "").trim();
  const state = String(match?.state || "").trim();
  const range = String(match?.rangeName || "").trim();

  if (city && state) return `${city}, ${state}`;
  if (range && state) return `${range}, ${state}`;
  if (range) return range;
  if (state) return state;
  return "Location not set";
}

function normalizeFirearmChoices(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }
  return [];
}

function buildRegistrationDraft(item = {}) {
  return {
    displayName: String(item.displayName || ""),
    email: String(item.email || ""),
    competitorNumber: String(item.competitorNumber || ""),
    nraMemberId: String(item.nraMemberId || ""),
    bullseyeNraCompetitorId: String(item.bullseyeNraCompetitorId || ""),
    classification: String(item.classification || ""),
    categoriesText: Array.isArray(item.categories) ? item.categories.join(", ") : String(item.category || ""),
    relayLabel: String(item.relayLabel || ""),
    firingPointLabel: String(item.firingPointLabel || ""),
    teamName: String(item.teamName || ""),
    dateOfBirth: String(item.dateOfBirth || ""),
    notes: String(item.notes || ""),
    firearmChoices: normalizeFirearmChoices(item.firearmChoices),
  };
}

function parseCategoriesText(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeRegistrationDraft(draft = {}) {
  return {
    displayName: String(draft.displayName || "").trim(),
    email: String(draft.email || "").trim(),
    competitorNumber: String(draft.competitorNumber || "").trim(),
    nraMemberId: String(draft.nraMemberId || "").trim(),
    bullseyeNraCompetitorId: String(draft.bullseyeNraCompetitorId || "").trim(),
    classification: String(draft.classification || "").trim(),
    categories: parseCategoriesText(draft.categoriesText),
    relayLabel: String(draft.relayLabel || "").trim(),
    firingPointLabel: String(draft.firingPointLabel || "").trim(),
    teamName: String(draft.teamName || "").trim(),
    dateOfBirth: String(draft.dateOfBirth || "").trim(),
    notes: String(draft.notes || "").trim(),
    firearmChoices: normalizeFirearmChoices(draft.firearmChoices),
  };
}

function registrationChanged(registration, draft) {
  const normalized = normalizeRegistrationDraft(draft);
  return (
    normalized.displayName !== String(registration?.displayName || "").trim() ||
    normalized.email !== String(registration?.email || "").trim() ||
    normalized.competitorNumber !== String(registration?.competitorNumber || "").trim() ||
    normalized.nraMemberId !== String(registration?.nraMemberId || "").trim() ||
    normalized.bullseyeNraCompetitorId !== String(registration?.bullseyeNraCompetitorId || "").trim() ||
    normalized.classification !== String(registration?.classification || "").trim() ||
    JSON.stringify(normalized.categories) !== JSON.stringify(Array.isArray(registration?.categories) ? registration.categories : []) ||
    normalized.relayLabel !== String(registration?.relayLabel || "").trim() ||
    normalized.firingPointLabel !== String(registration?.firingPointLabel || "").trim() ||
    normalized.teamName !== String(registration?.teamName || "").trim() ||
    normalized.dateOfBirth !== String(registration?.dateOfBirth || "").trim() ||
    normalized.notes !== String(registration?.notes || "").trim() ||
    JSON.stringify(normalized.firearmChoices) !== JSON.stringify(normalizeFirearmChoices(registration?.firearmChoices))
  );
}

function registrationSummaryCounts(rows) {
  return rows.reduce(
    (running, row) => {
      running.total += 1;
      const status = String(row?.status || "").trim().toLowerCase();
      if (status in running) {
        running[status] += 1;
      }
      return running;
    },
    { total: 0, pending: 0, confirmed: 0, cancelled: 0, withdrawn: 0 }
  );
}

function formatBullseyeScore(row) {
  const score = Number(row?.totalScore || 0);
  const xCount = Number(row?.xCount || 0);
  return xCount > 0 ? `${score}-${xCount}X` : String(score);
}

function buildSnapshotRosterRows(leaderboardRows = []) {
  return (Array.isArray(leaderboardRows) ? leaderboardRows : [])
    .filter((row) => String(row?.competitorId || "").trim())
    .map((row) => ({
      registrationId: `snapshot:${row.competitorId}`,
      competitorId: String(row.competitorId || "").trim(),
      displayName: String(row.competitorName || row.displayName || row.competitorId || "").trim(),
      competitorNumber: "",
      status: "confirmed",
      source: "ios_score_snapshot",
      isSnapshotRosterRow: true,
      scoreText: formatBullseyeScore(row),
      scoreProgressText: `${Number(row.completedScoreUnits || 0)}/${Number(row.totalScoreUnits || 0)}`,
      scoreStatus: String(row.status || "in_progress"),
      updatedAt: null,
    }));
}

function ScoringGroupSelection({ groups, value, onChange, error, assignedIds = [], required = true }) {
  return <fieldset className="bullseye-registration-enrollment">
    <legend>Scoring Firearm Groups{required ? " (Required)" : ""}</legend>
    {error ? <p role="alert">{error}</p> : groups.length === 0
      ? <p>Publish the match course structure before confirming participation.</p>
      : <div className="registration-checkbox-grid">{groups.map(group => (
        <label key={group.gunGroupId} className="registration-checkbox-option">
          <input type="checkbox" checked={assignedIds.includes(group.gunGroupId) || value.includes(group.gunGroupId)}
            disabled={assignedIds.includes(group.gunGroupId)}
            onChange={() => onChange(value.includes(group.gunGroupId)
              ? value.filter(id => id !== group.gunGroupId) : [...value, group.gunGroupId])} />
          {group.title}
        </label>
      ))}</div>}
  </fieldset>;
}

function BullseyeOwnedMatchRegistrations() {
  const location = useLocation();
  const { matchId } = useParams();
  const [ownerProfile, setOwnerProfile] = useState(null);
  const [match, setMatch] = useState(null);
  const [summary, setSummary] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [entries, setEntries] = useState([]);
  const [gunGroups, setGunGroups] = useState([]);
  const [enrollmentError, setEnrollmentError] = useState("");
  const [selectedGroups, setSelectedGroups] = useState({});
  const defaultGroupIds = gunGroups.length === 1 ? [gunGroups[0].gunGroupId] : [];
  const groupSelection = (key, participantEntries = []) => selectedGroups[key]
    ?? (participantEntries.some(entry => entry.metadata?.gunGroupId) ? [] : defaultGroupIds);
  const [draftById, setDraftById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionBusyKey, setActionBusyKey] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addRegistrationOpen, setAddRegistrationOpen] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState({
    status: "scheduled",
    publicationState: "published_private",
  });
  const [newRegistration, setNewRegistration] = useState({
    displayName: "",
    email: "",
    competitorNumber: "",
    nraMemberId: "",
    bullseyeNraCompetitorId: "",
    classification: "",
    categoriesText: "",
    relayLabel: "",
    firingPointLabel: "",
    teamName: "",
    dateOfBirth: "",
    notes: "",
    firearmChoices: [],
  });

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError("");
      setActionMessage("");

      try {
        const authProfile = await getCompetitionAuthProfile();
        const resolvedUserId = String(authProfile?.userId || "").trim();
        if (!resolvedUserId) {
          throw new Error("Missing user identity");
        }

        const [matchValue, summaryValue, registrationRows, leaderboardRows, entryRows, setup] = await Promise.all([
          getBullseyeMatch(matchId, { auth: true }),
          getBullseyeMatchRegistrationSummary(matchId),
          listAllBullseyeMatchRegistrations(matchId),
          listBullseyeMatchLeaderboards({ matchId }).catch(() => []),
          listCompetitionEntries(matchId),
          getBullseyeMatchSetupDetail(matchId, { auth: true }).catch(error => ({ error: error.message })),
        ]);
        const resolvedRegistrationRows = Array.isArray(registrationRows) && registrationRows.length > 0
          ? registrationRows
          : buildSnapshotRosterRows(leaderboardRows);

        if (!active) return;
        setGunGroups(setup?.setupDetail?.gunGroups || []);
        setEnrollmentError(setup?.error || "");

        setOwnerProfile({
          userId: resolvedUserId,
          email: String(authProfile?.email || "").trim(),
          displayName: String(authProfile?.displayName || "").trim(),
        });
        setMatch(matchValue);
        setSummary(summaryValue);
        setRegistrations(resolvedRegistrationRows);
        setEntries(entryRows);
        setDraftById(
          Object.fromEntries((resolvedRegistrationRows || []).map((row) => [row.competitorId, buildRegistrationDraft(row)]))
        );
        setSettingsDraft({
          status: String(matchValue?.status || "scheduled"),
          publicationState: String(matchValue?.publicationState || "published_private"),
        });
      } catch (err) {
        if (!active) return;
        const message = String(err?.message || "");
        setError(isAuthErrorMessage(message) ? "Sign in to manage Bullseye registrations." : (message || "Failed to load Bullseye match management"));
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [matchId]);

  const filteredRegistrations = useMemo(() => {
    const query = String(searchText || "").trim().toLowerCase();
    return registrations.filter((row) => {
      const matchesStatus = statusFilter === "all" || String(row?.status || "").toLowerCase() === statusFilter;
      if (!matchesStatus) return false;
      if (!query) return true;
      return [
        row?.displayName,
        row?.email,
        row?.competitorNumber,
        row?.bullseyeNraCompetitorId,
        row?.nraMemberId,
        ...(Array.isArray(row?.categories) ? row.categories : []),
        row?.teamName,
      ].some((value) => String(value || "").toLowerCase().includes(query));
    });
  }, [registrations, searchText, statusFilter]);

  const counts = useMemo(() => registrationSummaryCounts(registrations), [registrations]);
  const trimmedSearchText = String(searchText || "").trim();
  const hasRosterFilters = statusFilter !== "all" || Boolean(trimmedSearchText);
  const activeRosterFilterLabels = [
    statusFilter !== "all" ? formatStatus(statusFilter) : null,
    trimmedSearchText ? `Search: ${trimmedSearchText}` : null,
  ].filter(Boolean);

  function setDraftField(competitorId, field, value) {
    setDraftById((prev) => ({
      ...prev,
      [competitorId]: {
        ...(prev[competitorId] || buildRegistrationDraft()),
        [field]: value,
      },
    }));
  }

  function toggleDraftFirearm(competitorId, firearmTitle) {
    setDraftById((prev) => {
      const current = prev[competitorId] || buildRegistrationDraft();
      const currentChoices = normalizeFirearmChoices(current.firearmChoices);
      const nextChoices = currentChoices.includes(firearmTitle)
        ? currentChoices.filter((item) => item !== firearmTitle)
        : [...currentChoices, firearmTitle];
      return {
        ...prev,
        [competitorId]: {
          ...current,
          firearmChoices: nextChoices,
        },
      };
    });
  }

  function toggleNewFirearm(firearmTitle) {
    setNewRegistration((prev) => {
      const currentChoices = normalizeFirearmChoices(prev.firearmChoices);
      const nextChoices = currentChoices.includes(firearmTitle)
        ? currentChoices.filter((item) => item !== firearmTitle)
        : [...currentChoices, firearmTitle];
      return { ...prev, firearmChoices: nextChoices };
    });
  }

  async function refreshRegistrations(message = "") {
    const [summaryValue, registrationRows, leaderboardRows, entryRows] = await Promise.all([
      getBullseyeMatchRegistrationSummary(matchId),
      listAllBullseyeMatchRegistrations(matchId),
      listBullseyeMatchLeaderboards({ matchId }).catch(() => []),
      listCompetitionEntries(matchId),
    ]);
    const resolvedRegistrationRows = Array.isArray(registrationRows) && registrationRows.length > 0
      ? registrationRows
      : buildSnapshotRosterRows(leaderboardRows);
    setSummary(summaryValue);
    setRegistrations(resolvedRegistrationRows);
    setEntries(entryRows);
    setDraftById(
      Object.fromEntries((resolvedRegistrationRows || []).map((row) => [row.competitorId, buildRegistrationDraft(row)]))
    );
    setActionMessage(message);
  }

  async function handleSaveSettings() {
    setActionBusyKey("settings");
    setActionMessage("");
    setError("");
    try {
      const updated = await updateBullseyeMatch(matchId, {
        status: settingsDraft.status,
        publicationState: settingsDraft.publicationState,
      });
      setMatch(updated);
      setActionMessage("Match settings updated.");
    } catch (err) {
      setError(err?.message || "Failed to update Bullseye match settings");
    } finally {
      setActionBusyKey("");
    }
  }

  async function handleCreateRegistration(event) {
    event.preventDefault();
    if (!validBullseyeCompetitorId(newRegistration.bullseyeNraCompetitorId)) return;
    setActionBusyKey("create");
    setActionMessage("");
    setError("");
    try {
      if (enrollmentError) throw new Error(enrollmentError);
      const registrationId = crypto.randomUUID();
      const entries = bullseyeEnrollmentEntries(registrationId, gunGroups, groupSelection("new"));
      await createOwnerManagedBullseyeMatchRegistration(matchId, {
        ...normalizeRegistrationDraft(newRegistration), registrationId, entries,
      });
      setNewRegistration({
        displayName: "",
        email: "",
        competitorNumber: "",
        nraMemberId: "",
        bullseyeNraCompetitorId: "",
        classification: "",
        categoriesText: "",
        relayLabel: "",
        firingPointLabel: "",
        teamName: "",
        dateOfBirth: "",
        notes: "",
        firearmChoices: [],
      });
      setAddRegistrationOpen(false);
      await refreshRegistrations("Registration added.");
    } catch (err) {
      setError(err?.message || "Failed to add registration");
    } finally {
      setActionBusyKey("");
    }
  }

  async function handleSaveRegistration(competitorId) {
    const registration = registrations.find((row) => row.competitorId === competitorId);
    const draft = draftById[competitorId] || buildRegistrationDraft(registration);
    if (!validBullseyeCompetitorId(draft.bullseyeNraCompetitorId)) return;
    const registrationId = registration?.registrationId;
    if (!registrationId) {
      setError("Registration ID is missing. Refresh the roster before updating this row.");
      return;
    }
    setActionBusyKey(`save:${competitorId}`);
    setActionMessage("");
    setError("");
    try {
      const updated = await updateBullseyeMatchRegistration(matchId, registrationId, { ...normalizeRegistrationDraft(draft), expectedUpdatedAt: registration.updatedAt });
      if (updated?.registrationId !== registrationId || typeof updated.updatedAt !== "string" || !updated.updatedAt.trim()) {
        throw new Error("Registration update was not acknowledged. Refresh the roster before retrying.");
      }
      setRegistrations(previous => previous.map(row => row.registrationId === registrationId ? { ...row, ...updated } : row));
      setDraftById(current => {
        const next = buildRegistrationDraft({ ...registration, ...updated });
        const local = current[competitorId] || draft;
        // Keep edits made during the request, including explicit clears, against the accepted revision.
        for (const field of Object.keys(next)) {
          if (JSON.stringify(local[field]) !== JSON.stringify(draft[field])) next[field] = local[field];
        }
        return { ...current, [competitorId]: next };
      });
      setActionMessage("Registration updated.");
    } catch (err) {
      setError(err?.message || "Failed to update registration");
    } finally {
      setActionBusyKey("");
    }
  }

  async function handleConfirmRegistration(competitorId) {
    const registration = registrations.find((row) => row.competitorId === competitorId);
    const registrationId = registration?.registrationId;
    if (!registrationId) {
      setError("Registration ID is missing. Refresh the roster before confirming this row.");
      return;
    }
    setActionBusyKey(`confirm:${competitorId}`);
    setActionMessage("");
    setError("");
    try {
      if (enrollmentError) throw new Error(enrollmentError);
      const participantEntries = entries.filter(entry => entry.participantId === registration.participantId);
      await confirmBullseyeEnrollment({ registration, groups: gunGroups,
        selectedIds: groupSelection(registrationId, participantEntries), entries: participantEntries }, {
        updateEntry: (entryId, body) => updateCompetitionEntry(matchId, entryId, body),
        confirmRegistration: (id, body) => confirmBullseyeMatchRegistration(matchId, id, body),
        onEntriesUpdated: accepted => setEntries(previous => previous.map(entry =>
          accepted.find(updated => updated.entryId === entry.entryId) || entry)),
      });
      await refreshRegistrations("Registration confirmed.");
    } catch (err) {
      setError(err?.message || "Failed to confirm registration");
    } finally {
      setActionBusyKey("");
    }
  }

  async function handleCancelRegistration(competitorId) {
    const registration = registrations.find((row) => row.competitorId === competitorId);
    const registrationId = registration?.registrationId;
    if (!registrationId) {
      setError("Registration ID is missing. Refresh the roster before cancelling this row.");
      return;
    }
    setActionBusyKey(`cancel:${competitorId}`);
    setActionMessage("");
    setError("");
    try {
      await cancelBullseyeMatchRegistration(matchId, registrationId, registration.updatedAt);
      await refreshRegistrations("Registration cancelled.");
    } catch (err) {
      setError(err?.message || "Failed to cancel registration");
    } finally {
      setActionBusyKey("");
    }
  }

  const authGate = !loading && isAuthErrorMessage(error);
  const signInPath = buildAuthReturnPath(
    "/auth/sign-in",
    buildCurrentReturnTo(location, `/matches/manage/bullseye/${encodeURIComponent(matchId)}`)
  );

  return (
    <section className="leaderboard-page">
      <Helmet>
        <title>Registration Workbench | Range Pocket Competition</title>
      </Helmet>

      <Link to="/matches/manage/matches?family=bullseye" className="back-link">Owned Matches</Link>

      {loading && (
        <div className="leaderboard-card list-empty-panel status-loading-panel">
          <div className="owner-registration-section-label">Loading</div>
          <strong>Bullseye Workbench</strong>
        </div>
      )}
      {authGate && (
        <section className="leaderboard-card app-gate-panel">
          <div className="app-gate-eyebrow">Director access</div>
          <h2>Sign in to manage Bullseye registrations</h2>
          <div className="registration-auth-actions app-gate-actions">
            <Link to={signInPath} className="match-search-button leaderboard-inline-link">
              Sign In
            </Link>
          </div>
        </section>
      )}
      {!loading && error && !authGate && (
        <section className="leaderboard-card app-gate-panel director-error-panel">
          <div className="app-gate-eyebrow">Bullseye workbench</div>
          <h2>{match ? "Action could not complete" : "Registrations could not load"}</h2>
          <p>{error}</p>
          <div className="app-gate-actions">
            <button type="button" className="match-search-button" onClick={() => match ? setError("") : window.location.reload()}>
              {match ? "Dismiss" : "Try Again"}
            </button>
            <Link to="/matches/manage/matches" className="registration-auth-secondary leaderboard-inline-link">
              Owned Matches
            </Link>
            <Link to="/matches/manage" className="registration-auth-secondary leaderboard-inline-link">
              Director Overview
            </Link>
          </div>
        </section>
      )}

      {!loading && !authGate && match && (
        <>
          <div className="match-board-hero">
            <div>
              <h1>{match.matchName || "Bullseye Match"}</h1>
              <p>{buildLocationLabel(match)} • {formatDateTime(match.matchDate || match.startDate)}</p>
            </div>
            {ownerProfile?.displayName && <div className="leaderboard-meta">Owner: {ownerProfile.displayName}</div>}
          </div>

          <div className="leaderboard-card director-registration-summary-row">
            <span><strong>{counts.total}</strong> Total</span>
            <span><strong>{counts.pending}</strong> Pending</span>
            <span><strong>{counts.confirmed}</strong> Confirmed</span>
            <span><strong>{counts.cancelled}</strong> Cancelled</span>
            <span><strong>{counts.withdrawn}</strong> Withdrawn</span>
            <span><strong>{formatStatus(match.publicationState)}</strong> Visibility</span>
          </div>

          <section className="leaderboard-card director-registration-workbench">
            <div className="director-registration-workbench-copy">
              <div>
                <div className="owner-registration-section-label">Director Workbench</div>
                <h2>Roster controls</h2>
              </div>
            </div>
            <div className="director-registration-toolbar">
              {match.ownerUserId === ownerProfile?.userId && match.ownerUserId && <Link
                to={`/matches/manage/bullseye/${encodeURIComponent(matchId)}/audit`}
                className="registration-auth-secondary leaderboard-inline-link"
              >Action Audit</Link>}
              <Link to={`/matches/bullseye/${encodeURIComponent(matchId)}`} className="registration-auth-secondary leaderboard-inline-link">
                Public Page
              </Link>
              <button type="button" className="match-search-button" onClick={() => setSettingsOpen(true)}>
                Match Settings
              </button>
              <Link to={`/matches/manage/bullseye/${encodeURIComponent(matchId)}/results-settings`} className="registration-auth-secondary leaderboard-inline-link">Results Settings</Link>
              <button type="button" className="match-search-button" onClick={() => setAddRegistrationOpen(true)}>
                Add Competitor
              </button>
            </div>
            {actionMessage && (
              <div className="director-registration-messages">
                <div className="owner-banner-message owner-banner-success">{actionMessage}</div>
              </div>
            )}
          </section>

          {settingsOpen && (
          <details
            className="leaderboard-card owner-registration-panel director-add-participant-details bullseye-match-settings"
            open={settingsOpen}
            onToggle={(event) => setSettingsOpen(event.currentTarget.open)}
          >
            <summary>
              <span>
                <span className="owner-registration-section-label">Match</span>
                <strong>Publication and status</strong>
              </span>
              <span className="registration-auth-secondary">Open settings</span>
            </summary>
            <div className="owner-registration-create-grid">
              <label className="leaderboard-field">
                <span>Match Status</span>
                <select
                  className="match-sort"
                  value={settingsDraft.status}
                  onChange={(event) => setSettingsDraft((prev) => ({ ...prev, status: event.target.value }))}
                >
                  {MATCH_STATUS_OPTIONS.map((value) => (
                    <option key={value} value={value}>{formatStatus(value)}</option>
                  ))}
                </select>
              </label>
              <label className="leaderboard-field">
                <span>Publication</span>
                <select
                  className="match-sort"
                  value={settingsDraft.publicationState}
                  onChange={(event) => setSettingsDraft((prev) => ({ ...prev, publicationState: event.target.value }))}
                >
                  {PUBLICATION_STATE_OPTIONS.map((value) => (
                    <option key={value} value={value}>{formatStatus(value)}</option>
                  ))}
                </select>
              </label>
              <div className="leaderboard-field">
                <span>Registration Summary</span>
                <div className="leaderboard-meta">
                  {summary ? `${summary.confirmed ?? 0} confirmed • ${summary.pending ?? 0} pending` : "No summary yet"}
                </div>
              </div>
              <div className="owner-registration-form-actions owner-registration-settings-actions">
                <button
                  type="button"
                  className="match-search-button owner-registration-submit"
                  disabled={actionBusyKey === "settings"}
                  onClick={handleSaveSettings}
                >
                  {actionBusyKey === "settings" ? "Saving…" : "Save Match Settings"}
                </button>
              </div>
            </div>
          </details>
          )}

          {addRegistrationOpen && (
          <details
            className="leaderboard-card owner-registration-panel director-add-participant-details"
            open={addRegistrationOpen}
            onToggle={(event) => setAddRegistrationOpen(event.currentTarget.open)}
          >
            <summary>
              <span>
                <span className="owner-registration-section-label">Registration</span>
                <strong>Add competitor</strong>
              </span>
              <span className="registration-auth-secondary">Open form</span>
            </summary>
          <form className="director-add-participant-form bullseye-registration-form" onSubmit={handleCreateRegistration}>
            <div className="owner-registration-create-grid">
              <label className="leaderboard-field owner-registration-name-field">
                <span>Display Name</span>
                <input className="match-search" value={newRegistration.displayName} onChange={(event) => setNewRegistration((prev) => ({ ...prev, displayName: event.target.value }))} />
              </label>
              <label className="leaderboard-field">
                <span>Competitor Number</span>
                <input className="match-search" value={newRegistration.competitorNumber} onChange={(event) => setNewRegistration((prev) => ({ ...prev, competitorNumber: event.target.value }))} />
              </label>
              <BullseyeNraIdentityFields value={newRegistration}
                onChange={(field, value) => setNewRegistration(prev => ({ ...prev, [field]: value }))} />
              <label className="leaderboard-field">
                <span>Classification</span>
                <input className="match-search" value={newRegistration.classification} onChange={(event) => setNewRegistration((prev) => ({ ...prev, classification: event.target.value }))} />
              </label>
              <label className="leaderboard-field">
                <span>Relay</span>
                <input className="match-search" value={newRegistration.relayLabel} onChange={(event) => setNewRegistration((prev) => ({ ...prev, relayLabel: event.target.value }))} />
              </label>
              <label className="leaderboard-field">
                <span>Firing Point</span>
                <input className="match-search" value={newRegistration.firingPointLabel} onChange={(event) => setNewRegistration((prev) => ({ ...prev, firingPointLabel: event.target.value }))} />
              </label>
            </div>
            <ScoringGroupSelection groups={gunGroups} value={groupSelection("new")} error={enrollmentError}
              onChange={value => setSelectedGroups(previous => ({ ...previous, new: value }))} />
            <details className="owner-registration-optional-details">
              <summary>Optional Details</summary>
              <div className="owner-registration-create-grid owner-registration-optional-grid">
                <label className="leaderboard-field">
                  <span>Email</span>
                  <input className="match-search" value={newRegistration.email} onChange={(event) => setNewRegistration((prev) => ({ ...prev, email: event.target.value }))} />
                </label>
                <label className="leaderboard-field">
                  <span>Categories</span>
                  <input className="match-search" value={newRegistration.categoriesText} onChange={(event) => setNewRegistration((prev) => ({ ...prev, categoriesText: event.target.value }))} placeholder="Civilian, Woman, Senior" />
                </label>
                <label className="leaderboard-field">
                  <span>Team</span>
                  <input className="match-search" value={newRegistration.teamName} onChange={(event) => setNewRegistration((prev) => ({ ...prev, teamName: event.target.value }))} />
                </label>
                <label className="leaderboard-field">
                  <span>Date of Birth</span>
                  <input className="match-search" type="date" value={newRegistration.dateOfBirth} onChange={(event) => setNewRegistration((prev) => ({ ...prev, dateOfBirth: event.target.value }))} />
                </label>
                <label className="leaderboard-field owner-registration-affiliations-field">
                  <span>Notes</span>
                  <input className="match-search" value={newRegistration.notes} onChange={(event) => setNewRegistration((prev) => ({ ...prev, notes: event.target.value }))} />
                </label>
                <div className="leaderboard-field owner-registration-affiliations-field">
                    <span>Registration Firearm Requests (Optional)</span>
                  <div className="registration-checkbox-grid">
                    {FIREARM_CHOICES.map((choice) => (
                      <label key={choice} className="registration-checkbox-option">
                        <input
                          type="checkbox"
                          checked={newRegistration.firearmChoices.includes(choice)}
                          onChange={() => toggleNewFirearm(choice)}
                        />
                        {choice}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </details>
            <div className="owner-registration-form-actions">
              <button className="match-search-button owner-registration-submit" type="submit" disabled={actionBusyKey === "create" || !validBullseyeCompetitorId(newRegistration.bullseyeNraCompetitorId) || !groupSelection("new").length || !!enrollmentError}>
                {actionBusyKey === "create" ? "Adding…" : "Add Competitor"}
              </button>
            </div>
          </form>
          </details>
          )}

          <div className="leaderboard-card owner-registration-panel">
            <div className="owner-registration-list-toolbar director-roster-controls">
              <div>
                <div className="owner-registration-section-label">Registrations</div>
                <h2>Manage competitors</h2>
              </div>
              <input
                className="match-search"
                type="text"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Search name, NRA IDs, categories, team"
              />
              <label className="match-filter-field owner-registration-status-filter">
                <span>Status</span>
                <select className="match-sort" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                  {STATUS_FILTERS.map((status) => (
                    <option key={status} value={status}>{status === "all" ? "All statuses" : formatStatus(status)}</option>
                  ))}
                </select>
              </label>
              {hasRosterFilters && (
                <button
                  type="button"
                  className="registration-auth-secondary leaderboard-inline-link owner-registration-clear-filter"
                  onClick={() => {
                    setSearchText("");
                    setStatusFilter("all");
                  }}
                >
                  Reset
                </button>
              )}
            </div>

            {actionMessage && <div className="owner-banner-message owner-banner-success">{actionMessage}</div>}

            <div className="bullseye-registration-stack">
              {filteredRegistrations.map((registration) => {
                const draft = draftById[registration.competitorId] || buildRegistrationDraft(registration);
                const dirty = registrationChanged(registration, draft);
                const statusText = String(registration.status || "unknown").toLowerCase();
                const isSnapshotRosterRow = registration.isSnapshotRosterRow === true;
                const participantEntries = entries
                  .filter((entry) => registration.participantId && entry.participantId === registration.participantId)
                  .sort((a, b) => String(a.entryLabel || "").localeCompare(String(b.entryLabel || ""), undefined, { numeric: true }));
                const assignedGroupIds = participantEntries.map(entry => entry.metadata?.gunGroupId).filter(Boolean);
                const needsGroupAssignment = !participantEntries.length || participantEntries.some(entry => !entry.metadata?.gunGroupId);
                return (
                  <details key={registration.registrationId} className={`leaderboard-card bullseye-registration-card owner-registration-card owner-registration-card-${statusText}`}>
                    <summary className="bullseye-registration-summary">
                      <div>
                        <strong>{registration.displayName}</strong>
                        <RegistrationOrigin source={registration.source} />
                        <div className="leaderboard-meta">
                          {registration.competitorNumber ? `#${registration.competitorNumber}` : "No competitor number"}
                          {registration.teamName ? ` • ${registration.teamName}` : ""}
                          {isSnapshotRosterRow ? ` • Synced from iOS scorecards • ${registration.scoreText} • ${registration.scoreProgressText}` : ""}
                        </div>
                      </div>
                      <div className="bullseye-registration-summary-right">
                        <span className={`status-pill status-${statusText}`}>{formatStatus(registration.status)}</span>
                      </div>
                    </summary>

                    <div className="owner-registration-create-grid">
                      <label className="leaderboard-field owner-registration-name-field">
                        <span>Display Name</span>
                        <input className="match-search" value={draft.displayName} onChange={(event) => setDraftField(registration.competitorId, "displayName", event.target.value)} />
                      </label>
                      <label className="leaderboard-field">
                        <span>Competitor Number</span>
                        <input className="match-search" value={draft.competitorNumber} onChange={(event) => setDraftField(registration.competitorId, "competitorNumber", event.target.value)} />
                      </label>
                      <BullseyeNraIdentityFields value={draft}
                        onChange={(field, value) => setDraftField(registration.competitorId, field, value)} />
                      <label className="leaderboard-field">
                        <span>Classification</span>
                        <input className="match-search" value={draft.classification} onChange={(event) => setDraftField(registration.competitorId, "classification", event.target.value)} />
                      </label>
                      <label className="leaderboard-field">
                        <span>Relay</span>
                        <input className="match-search" value={draft.relayLabel} onChange={(event) => setDraftField(registration.competitorId, "relayLabel", event.target.value)} />
                      </label>
                      <label className="leaderboard-field">
                        <span>Firing Point</span>
                        <input className="match-search" value={draft.firingPointLabel} onChange={(event) => setDraftField(registration.competitorId, "firingPointLabel", event.target.value)} />
                      </label>
                    </div>

                    {registration.participantId && (
                      <section className="bullseye-registration-entries" aria-label="Scoring Entries">
                        <h3>Scoring Entries</h3>
                        {participantEntries.length === 0 ? (
                          <p className="leaderboard-meta">No confirmed scoring entries</p>
                        ) : (
                          <ul>
                            {participantEntries.map((entry) => (
                              <li key={entry.entryId}>
                                <span>{entry.entryLabel || "Unnamed entry"}</span>
                                <strong>{entry.status !== "active" ? formatStatus(entry.status)
                                  : entry.countsForLeaderboard ? "Primary" : "Practice"}</strong>
                              </li>
                            ))}
                          </ul>
                        )}
                      </section>
                    )}
                    {(needsGroupAssignment || gunGroups.some(group => !assignedGroupIds.includes(group.gunGroupId))) && (
                      <ScoringGroupSelection groups={gunGroups} value={groupSelection(registration.registrationId, participantEntries)} error={enrollmentError}
                        assignedIds={assignedGroupIds} required={needsGroupAssignment}
                        onChange={value => setSelectedGroups(previous => ({ ...previous, [registration.registrationId]: value }))} />
                    )}

                    <details className="owner-registration-optional-details">
                      <summary>Optional Details</summary>
                      <div className="owner-registration-create-grid owner-registration-optional-grid">
                        <label className="leaderboard-field">
                          <span>Email</span>
                          <input className="match-search" value={draft.email} onChange={(event) => setDraftField(registration.competitorId, "email", event.target.value)} />
                        </label>
                        <label className="leaderboard-field">
                          <span>Categories</span>
                          <input className="match-search" value={draft.categoriesText} onChange={(event) => setDraftField(registration.competitorId, "categoriesText", event.target.value)} placeholder="Civilian, Woman, Senior" />
                        </label>
                        <label className="leaderboard-field">
                          <span>Team</span>
                          <input className="match-search" value={draft.teamName} onChange={(event) => setDraftField(registration.competitorId, "teamName", event.target.value)} />
                        </label>
                        <label className="leaderboard-field">
                          <span>Date of Birth</span>
                          <input className="match-search" type="date" value={draft.dateOfBirth} onChange={(event) => setDraftField(registration.competitorId, "dateOfBirth", event.target.value)} />
                        </label>
                        <label className="leaderboard-field owner-registration-affiliations-field">
                          <span>Notes</span>
                          <input className="match-search" value={draft.notes} onChange={(event) => setDraftField(registration.competitorId, "notes", event.target.value)} />
                        </label>
                        <div className="leaderboard-field owner-registration-affiliations-field">
                          <span>Registration Firearm Requests (Optional)</span>
                          <div className="registration-checkbox-grid">
                            {FIREARM_CHOICES.map((choice) => (
                              <label key={choice} className="registration-checkbox-option">
                                <input
                                  type="checkbox"
                                  checked={draft.firearmChoices.includes(choice)}
                                  onChange={() => toggleDraftFirearm(registration.competitorId, choice)}
                                />
                                {choice}
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </details>

                    <div className="bullseye-registration-actions">
                      {isSnapshotRosterRow && (
                        <div className="owner-banner-message">
                          This iOS-synced competitor is shown from scorecard data. Add a web registration if you need editable roster fields.
                        </div>
                      )}
                      <button
                        type="button"
                        className="match-search-button"
                        disabled={isSnapshotRosterRow || !dirty || !validBullseyeCompetitorId(draft.bullseyeNraCompetitorId) || actionBusyKey === `save:${registration.competitorId}`}
                        onClick={() => handleSaveRegistration(registration.competitorId)}
                      >
                        {actionBusyKey === `save:${registration.competitorId}` ? "Saving…" : "Save"}
                      </button>
                      <button
                        type="button"
                        className="match-search-button"
                        disabled={isSnapshotRosterRow || actionBusyKey === `confirm:${registration.competitorId}`}
                        onClick={() => handleConfirmRegistration(registration.competitorId)}
                      >
                        {actionBusyKey === `confirm:${registration.competitorId}` ? "Confirming…" : "Confirm"}
                      </button>
                      <button
                        type="button"
                        className="match-search-button button-danger"
                        disabled={isSnapshotRosterRow || actionBusyKey === `cancel:${registration.competitorId}`}
                        onClick={() => handleCancelRegistration(registration.competitorId)}
                      >
                        {actionBusyKey === `cancel:${registration.competitorId}` ? "Cancelling…" : "Cancel"}
                      </button>
                    </div>

                    <div className="leaderboard-meta">
                      Updated {formatDateTime(registration.updatedAt || registration.createdAt)}
                    </div>
                  </details>
                );
              })}
              {filteredRegistrations.length === 0 && (
                <div className="leaderboard-card director-empty-state list-empty-panel">
                  <div className="owner-registration-section-label">No results</div>
                  <strong>{registrations.length === 0 ? "No registrations yet" : "No registrations found"}</strong>
                  {registrations.length === 0 ? (
                    <div className="empty-filter-summary" aria-label="Roster state">
                      <span>Roster: 0</span>
                      <span>Bullseye</span>
                    </div>
                  ) : activeRosterFilterLabels.length > 0 && (
                    <div className="empty-filter-summary" aria-label="Active filters">
                      {activeRosterFilterLabels.map((label) => (
                        <span key={label}>{label}</span>
                      ))}
                    </div>
                  )}
                  <div className="director-action-row">
                    {registrations.length === 0 && (
                      <button type="button" className="match-search-button" onClick={() => setAddRegistrationOpen(true)}>
                        Add Competitor
                      </button>
                    )}
                    {registrations.length > 0 && hasRosterFilters && (
                      <button
                        type="button"
                        className="registration-auth-secondary leaderboard-inline-link detail-link-button"
                        onClick={() => {
                          setSearchText("");
                          setStatusFilter("all");
                        }}
                      >
                        Reset
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

export const BullseyeOwnedMatchRegistrations_Page = {
  path: "/matches/manage/bullseye/:matchId",
  element: <BullseyeOwnedMatchRegistrations />,
};
